/**
 * Zustand 인증 상태 관리 스토어
 */

import { create } from 'zustand';
import { User } from '../types/user';
import { AuthResponse } from '../types/auth';
import {
  setAccessToken,
  setRefreshToken,
  setUser,
  getAccessToken,
  getRefreshToken,
  getUser,
  clearAuthData,
} from '../utils/storage';

/**
 * 인증 스토어 상태 인터페이스
 */
interface AuthState {
  // 상태
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setAuth: (user: User, tokens: AuthResponse) => void;
  setUser: (user: User) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  initAuth: () => void;
}

/**
 * Zustand 인증 스토어
 */
export const useAuthStore = create<AuthState>((set) => ({
  // 초기 상태
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  /**
   * 인증 정보 설정 (로그인 성공 시)
   */
  setAuth: (user: User, tokens: AuthResponse) => {
    // LocalStorage에 저장
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
    setUser(user);

    // 상태 업데이트
    set({
      user,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  },

  /**
   * 사용자 정보만 업데이트
   */
  setUser: (user: User) => {
    setUser(user);
    set({ user });
  },

  /**
   * 로딩 상태 설정
   */
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  /**
   * 에러 설정
   */
  setError: (error: string | null) => {
    set({ error, isLoading: false });
  },

  /**
   * 로그아웃
   */
  logout: () => {
    // LocalStorage 클리어
    clearAuthData();

    // 상태 초기화
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  /**
   * 초기 인증 상태 복원 (새로고침 시)
   */
  initAuth: () => {
    const token = getAccessToken();
    const refreshToken = getRefreshToken();
    const user = getUser();

    if (token && user) {
      set({
        user,
        accessToken: token,
        refreshToken: refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } else {
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },
}));
